import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { usePosts } from '../hooks/usePosts';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import Layout from '../components/layout/Layout';
import { Button, LoadingState, ErrorState, EmptyState } from '../components/ui';
import { PostCard, MasonryGrid, CreatePostModal } from '../components/posts';
import { getHeroImageUrls } from '../services/postService';
import { getHeroPortfolioImageUrls } from '../services/portfolioService';
import { Hero, TrendingArtists, FeaturedPosts, RecentActivity } from '../components/home';
import { CATEGORIES } from '../constants/categories';
import { SeriesRail } from '../components/series';

// Opciones de filtro de feed
const FEED_FILTERS = [
  { id: 'explore', name: 'Para ti (Explorar)' },
  { id: 'following', name: 'Siguiendo' },
  { id: 'popular', name: 'Popular' }
];

const Home = () => {
  const { userData } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('explore'); // 'explore' | 'following' | 'popular'
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);

  const [heroImageUrls, setHeroImageUrls] = useState([]);
  const { posts, loading, error, hasMore, loadMore, setPosts, refresh } = usePosts(filter, userData?.id);
  const blockedIds = userData?.blocked || [];
  const blockedFilteredPosts = (posts || []).filter((p) => !blockedIds.includes(p.userId));
  const categoryFilteredPosts =
    selectedCategory === 'all'
      ? blockedFilteredPosts
      : blockedFilteredPosts.filter((p) => p?.category === selectedCategory);

  // Hero: cargar imágenes de portfolio (o de posts si hay pocas)
  useEffect(() => {
    let cancelled = false;
    getHeroPortfolioImageUrls(15)
      .then((urls) => {
        if (cancelled) return;
        if (Array.isArray(urls) && urls.length >= 3) {
          setHeroImageUrls(urls);
        } else {
          getHeroImageUrls(15).then((fallback) => {
            if (!cancelled && Array.isArray(fallback)) setHeroImageUrls(fallback);
          });
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Detectar query param ?create=post o state para abrir modal
  useEffect(() => {
    const createParam = searchParams.get('create');
    const locationState = location.state;

    if (createParam === 'post' || locationState?.openCreateModal) {
      setShowCreatePostModal(true);
      // Limpiar query param / state para que no se vuelva a abrir al usar "Atrás"
      if (createParam === 'post') {
        window.history.replaceState({}, '', '/');
      }
      if (locationState?.openCreateModal) {
        navigate('/', { replace: true, state: {} });
      }
    }
  }, [searchParams, location.state, navigate]);

  return (
    <Layout>
      {showCreatePostModal && (
        <CreatePostModal
          onClose={() => setShowCreatePostModal(false)}
          onPostCreated={(post) => {
            setPosts((prev) => [post, ...(prev || [])]);
            setShowCreatePostModal(false);
          }}
        />
      )}
      <div className="py-6">
        {/* Hero / Banner */}
        <Hero imageUrls={heroImageUrls} title="Illustra" subtitle="Donde el arte se encuentra" extraText="Inspira. Crea. Comparte." />

        {/* Artistas destacados (debajo del banner) */}
        <div className="container-custom mt-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
              Artistas destacados
            </h2>
          </div>
          <TrendingArtists variant="circles" limitCount={24} />
        </div>

        {/* Series (tipo Webtoon) */}
        <div className="container-custom">
          <SeriesRail title="Series destacadas" mode="trending" limitCount={12} />
          <SeriesRail title="Series nuevas" mode="new" limitCount={12} />
        </div>

        {/* Filtros: Dropdown de feed type + Categorías */}
        <div className="container-custom mb-6 mt-8">
          <div className="flex items-start gap-4 flex-wrap">
            {/* Dropdown de tipo de feed */}
            <Menu as="div" className="relative flex-shrink-0">
              <MenuButton className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg font-medium text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors shadow-sm">
                <span>{FEED_FILTERS.find(f => f.id === filter)?.name || 'Para ti (Explorar)'}</span>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </MenuButton>

              <MenuItems className="absolute left-0 mt-2 w-64 origin-top-left bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl py-1 z-50 focus:outline-none">
                {FEED_FILTERS.map((feedFilter) => (
                  <MenuItem key={feedFilter.id}>
                    {({ active }) => (
                      <button
                        onClick={() => setFilter(feedFilter.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm ${
                          active
                            ? 'bg-gray-100 dark:bg-slate-700'
                            : ''
                        } ${
                          filter === feedFilter.id
                            ? 'text-indigo-600 dark:text-indigo-400 font-medium'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <span>{feedFilter.name}</span>
                        {filter === feedFilter.id && (
                          <svg className="w-5 h-5 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    )}
                  </MenuItem>
                ))}
              </MenuItems>
            </Menu>

            {/* Chips de categorías */}
            <div className="flex-1 flex gap-2 overflow-x-auto pb-2 hide-scrollbar min-w-0">
              {CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`
                    px-4 py-2 rounded-full whitespace-nowrap font-medium text-sm transition-all flex-shrink-0
                    ${selectedCategory === category.id
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 border border-gray-200 dark:border-slate-700'
                    }
                  `}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Feed Container */}
        <div className="container-custom">
          {/* Posts destacados */}
          <FeaturedPosts selectedCategory={selectedCategory} />

          {/* Actividad reciente */}
          {filter === 'following' && (
            <RecentActivity selectedCategory={selectedCategory} />
          )}

          {/* Columna principal - Posts Grid */}
          <div className="flex-1 min-w-0">
              {/* Loading inicial */}
              {loading && posts.length === 0 && (
                <LoadingState message="Cargando posts..." />
              )}

              {/* Error */}
              {error && (
                <ErrorState
                  message={error}
                  onAction={refresh}
                  actionLabel="Reintentar"
                />
              )}

              {/* Posts Grid */}
              {!loading && categoryFilteredPosts.length > 0 && (
                <>
                  <MasonryGrid>
                    {categoryFilteredPosts.map((post) => (
                      <PostCard
                        key={post.id}
                        post={post}
                        currentUserId={userData?.id}
                        savedPostIds={userData?.savedPosts || []}
                      />
                    ))}
                  </MasonryGrid>

                  {/* Botón cargar más */}
                  {hasMore && (
                    <div className="text-center mt-8">
                      <Button
                        variant="outline"
                        onClick={loadMore}
                        loading={loading}
                      >
                        Cargar más posts
                      </Button>
                    </div>
                  )}
                </>
              )}

              {/* Empty state */}
              {!loading && categoryFilteredPosts.length === 0 && (
                <EmptyState
                  title="No hay posts todavía"
                  description={
                    filter === 'following'
                      ? 'Sigue a algunos artistas para ver su contenido aquí'
                      : 'Sé el primero en compartir tu arte'
                  }
                  icon={
                    <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  }
                  actionLabel="Crear tu primer post"
                  onAction={() => setShowCreatePostModal(true)}
                />
              )}
          </div>
        </div>
      </div>

    </Layout>
  );
};

export default Home;
